<template>
  <!--
    Lo que de verdad pasó, del módulo y no del modelo: aunque la frase del
    asistente dijera otra cosa, esto es lo que se guardó. Una lista de
    escrituras enseña una fila por cada una, con su estado; lo que se puede
    deshacer lleva su «Deshacer» mientras dura su ventana.
  -->
  <div class="result" :class="outcome.ok ? 'result--ok' : 'result--failed'" role="status">
    <div class="result__line">
      <span aria-hidden="true">{{ mark(outcome) }}</span>
      <span class="result__text">{{ textOf(outcome) }}</span>
      <RouterLink v-if="outcome.ok && outcome.route" :to="outcome.route" class="result__open">
        {{ t('assistant.outcome.open') }}
      </RouterLink>
      <UndoButton v-if="!rows.length" :entry="outcome" />
    </div>
    <!-- Lo que se adjuntó en el chat y quedó colgado del registro. -->
    <p v-if="attached.length" class="result__attached">
      📎 {{ t('assistant.attach.attached', { files: attached.join(', ') }) }}
    </p>

    <ul v-if="rows.length" class="result__rows">
      <li v-for="(row, index) in rows" :key="index" class="result__row" :class="`result__row--${row.status}`">
        <span aria-hidden="true">{{ mark(row) }}</span>
        <span class="result__text">{{ textOf(row) }}</span>
        <RouterLink v-if="row.ok && row.route" :to="row.route" class="result__open">{{ t('assistant.outcome.open') }}</RouterLink>
        <UndoButton :entry="row" />
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, defineComponent, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { errorMessage } from '@general/ui-kit'

import { useAssistantStore } from '../store'

const props = defineProps({
  outcome: { type: Object, required: true },
})

const { t, te } = useI18n()
const store = useAssistantStore()

const rows = computed(() => props.outcome.items ?? [])
// De la escritura o de cada fila de una lista; cada fichero una vez.
const attached = computed(() => [...new Set([props.outcome, ...rows.value]
  .flatMap(entry => entry.attached ?? []).map(file => file.name))])

/** Un Phrase del servidor ({ text, key, params }) en el idioma de quien mira. */
const say = (phrase) => {
  if (!phrase) return ''
  if (typeof phrase === 'string') return phrase
  return phrase.key && te(phrase.key) ? t(phrase.key, phrase.params ?? {}) : (phrase.text ?? '')
}

/** Por qué falló, traducido por su código cuando lo trae; si no, lo que dijo el servidor. */
const why = (entry) => (entry?.errorCode
  ? errorMessage({ code: entry.errorCode, details: entry.errorDetails ?? null, message: entry.error ?? '' }, t)
  : (entry?.error ?? ''))

function mark(entry) {
  if (entry.undone) return '↶'
  if (entry.ok) return '✓'
  if (entry.effect === 'unknown') return '?'
  if (entry.status === 'dropped' || entry.status === 'skipped') return '–'
  return '×'
}

function textOf(entry) {
  if (entry.undone) return `${say(entry.text) || t('assistant.outcome.done')} · ${t('assistant.outcome.undone')}`
  if (entry.ok) return say(entry.text) || t('assistant.outcome.done')
  if (entry.status === 'dropped' || entry.status === 'skipped') return `${entry.tool ?? ''} · ${t(`assistant.card.status.${entry.status}`)}`
  if (entry.effect === 'unknown') return t('assistant.outcome.unknown')
  return entry.error ? t('assistant.outcome.failedWith', { error: why(entry) }) : t('assistant.outcome.failed')
}

/** «Deshacer» mientras dure su ventana; lo que contestó el servidor, después. */
const UndoButton = defineComponent({
  props: { entry: { type: Object, required: true } },
  setup(inner) {
    return () => {
      const entry = inner.entry
      const undo = entry.undo
      if (!undo?.id || !entry.ok) return null
      if (entry.undoResult && !entry.undoResult.ok) {
        return h('span', { class: 'result__undo-failed' },
          t('assistant.outcome.undoFailed', { error: why(entry.undoResult) }))
      }
      if (entry.undone || Date.parse(undo.until) < Date.now()) return null
      return h('button', {
        type: 'button',
        class: 'result__undo',
        disabled: store.undoing.includes(undo.id),
        onClick: () => store.undo(entry),
      }, t('assistant.outcome.undo'))
    }
  },
})
</script>

<style scoped>
.result {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: var(--space-2);
  padding: 6px var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}
.result--ok { background: var(--color-success-soft); color: var(--color-success); }
.result--failed { background: var(--color-danger-soft, var(--color-gray-100)); color: var(--color-danger); }
.result__line, .result__row { display: flex; align-items: center; gap: var(--space-2); }
.result__attached { margin: var(--space-1) 0 0; font-size: var(--text-xs); color: var(--color-text-soft); overflow-wrap: anywhere; }
.result__rows { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 2px; }
.result__row--dropped, .result__row--skipped { opacity: 0.7; }
.result__text { flex: 1; color: var(--color-text); overflow-wrap: anywhere; }
.result__open { font-weight: 600; color: inherit; }
.result__undo {
  flex: none;
  background: none;
  border: 0;
  padding: 0;
  color: var(--color-primary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.result__undo:disabled { opacity: 0.5; cursor: default; }
.result__undo-failed { color: var(--color-danger); font-size: var(--text-xs); }
</style>
